import { useState } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';
import { DragAndDropInput } from '~/components/ui/inputs/DragAndDropInput';
import type { ImportResult } from '~/utils/importResult';
import { MAX_UPLOAD_SIZE_MB } from '~/utils/uploadError';

type UploadCSVModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  loading: boolean;
  importResult?: ImportResult | null;
  requiredFieldsText: string;
  handleDownloadTemplate: () => void;
  handleDownloadTemplateWithData?: () => void;
};

export function UploadCSVModal({
  isOpen,
  onClose,
  onUpload,
  file,
  onFileChange,
  loading,
  importResult,
  requiredFieldsText,
  handleDownloadTemplate,
  handleDownloadTemplateWithData,
}: UploadCSVModalProps) {
  const { t } = useTranslation('general');
  const [error, setError] = useState<string | undefined>();

  const handleFileChange = (selected: File) => {
    setError(undefined);
    onFileChange(selected);
  };

  const handleClose = () => {
    onFileChange(null);
    setError(undefined);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-xl shadow-xl w-[35rem] max-w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neut-border">
          <h2 className="text-base font-semibold text-neut-prim">
            {t('upload_csv_modal.title')}
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-neut-sec hover:bg-neut-hover hover:text-neut-prim transition-colors"
            aria-label={t('upload_csv_modal.close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-neut-sec">
            {t('upload_csv_modal.description')}
          </p>
          <DragAndDropInput
            accept=".csv"
            maxSizeMB={MAX_UPLOAD_SIZE_MB}
            file={file}
            onChange={handleFileChange}
            onRemove={() => onFileChange(null)}
            onError={setError}
          />
          {error && <p className="text-xs text-error-600">{error}</p>}
          <div className="bg-neut-50 rounded-lg p-4 flex flex-col gap-3">
            <p className="text-sm font-medium text-neut-700">
              {t('upload_csv_modal.template.title')}
            </p>
            <div className="flex items-start justify-between">
              <Button
                variant="ghost"
                size="sm"
                width="auto"
                onClick={handleDownloadTemplate}
              >
                <Download size={14} />
                {t('upload_csv_modal.template.download')}
              </Button>

              {handleDownloadTemplateWithData && (
                <Button
                  size="sm"
                  onClick={handleDownloadTemplateWithData}
                  variant="ghost-primary"
                  width="auto"
                >
                  <div>
                    <div className="flex flex-row gap-3 items-center justify-center">
                      {' '}
                      <Download size={14} />
                      {t('upload_csv_modal.template.download_with_data')}
                    </div>

                    <span className="text-xs text-neut-500 font-normal">
                      (Downloads currently filtered data)
                    </span>
                  </div>
                </Button>
              )}
            </div>
            <p className="text-xs text-neut-600">{requiredFieldsText}</p>
          </div>

          {importResult && (
            <div className="space-y-2">
              <p className="text-sm text-neut-sec">
                <span className="font-medium text-neut-prim">
                  {importResult.totalRows}
                </span>{' '}
                {importResult.totalRows !== 1
                  ? t('upload_csv_modal.result.rows')
                  : t('upload_csv_modal.result.row')}{' '}
                {t('upload_csv_modal.result.processed')},{' '}
                <span className="font-medium text-neut-prim">
                  {importResult.updatedRows}
                </span>{' '}
                {t('upload_csv_modal.result.updated')}
                {importResult.errors.length > 0 && (
                  <span className="text-error-600">
                    , {importResult.errors.length}{' '}
                    {t('upload_csv_modal.result.with_errors')}
                  </span>
                )}
              </p>
              {importResult.errors.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {importResult.errors.map((row, i) => {
                    const hasIdentifier =
                      row.productCode || row.length || row.location;
                    return (
                      <div
                        key={i}
                        className="border border-error-300 rounded-lg px-3 py-2 bg-error-50"
                      >
                        <p className="text-xs font-medium text-error-700 mb-1">
                          {hasIdentifier ? (
                            <>
                              {t('upload_csv_modal.result.row_number')}{' '}
                              {row.rowNumber}
                              <span className="text-neut-400">
                                {' '}
                                (
                                {[row.productCode, row.length, row.location]
                                  .filter(Boolean)
                                  .join(' · ')}
                                )
                              </span>
                            </>
                          ) : (
                            t('upload_csv_modal.result.file_level_error')
                          )}
                        </p>
                        <ul className="space-y-0.5">
                          {row.errors.map((msg, j) => (
                            <li key={j} className="text-xs text-error-600">
                              • {msg}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neut-border">
          <Button variant="accent" size="sm" width="auto" onClick={handleClose}>
            {t('upload_csv_modal.buttons.cancel')}
          </Button>
          <Button
            size="sm"
            width="auto"
            onClick={() => onUpload(file!)}
            disabled={!file || loading}
            loading={loading}
          >
            {t('upload_csv_modal.buttons.upload')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
